import { FC, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { Controller } from 'react-hook-form';
import { ReactComponent as TriangleIcon } from 'assets/icons/triangle.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/drop-down.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/add_circle_outline.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import ReactTooltip from 'react-tooltip';
import { isNumber } from 'lodash';

interface AvoidBookingWithinTimeFrameSectionProps {
  control: any;
  setValue: any;
}

const EXISTING_APPOINTMENT_WINDOW_DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map(
  (days) => ({
    label: days === 1 ? `${days} day` : `${days} days`,
    value: days,
  })
);

const AvoidBookingWithinTimeFrameSection: FC<
  AvoidBookingWithinTimeFrameSectionProps
> = ({ control, setValue }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="flex flex-col gap-y-1.4">
      <button
        className="flex items-center gap-x-0.8 text-left"
        onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
      >
        <TriangleIcon
          className={`${
            isExpanded ? '' : '-rotate-90'
          } transition-transform w-1.6 h-1.6 shrink-0`}
        />
        <span className="font-bold text-16 leading-[2.2rem] text-darkest-grey">
          Prevent Online Rebooking Within{' '}
          <span className="whitespace-nowrap">
            Timeframe
            <span
              data-for="avoid-booking-timeframe-info"
              data-tip=""
              className="inline-block align-middle ml-0.8"
            >
              <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
            </span>
          </span>
        </span>
        <ReactTooltip
          id="avoid-booking-timeframe-info"
          effect="solid"
          place="bottom"
          className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
        >
          <span className="text-11 text-darkest-grey flex items-center font-normal">
            Set the number of days within which patients cannot book a new
            appointment if they already have one scheduled.
          </span>
        </ReactTooltip>
      </button>
      {isExpanded && (
        <Controller
          name="futureAppointmentWindowDays"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-y-0.8">
              <Listbox
                {...field}
                onChange={(value: number | null) => {
                  setValue('futureAppointmentWindowDays', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              >
                {({ open }) => (
                  <div className="relative text-14 leading-[2.1rem]">
                    <Listbox.Button
                      className={`relative w-full h-[4.8rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active outline-none ${
                        open ? 'border-magenta shadow-input-active' : ''
                      }`}
                    >
                      <span
                        className={`block pr-3 ${
                          isNumber(field.value)
                            ? 'text-darkest-grey'
                            : 'text-mid-grey'
                        }`}
                      >
                        {isNumber(field.value)
                          ? EXISTING_APPOINTMENT_WINDOW_DAYS_OPTIONS.find(
                              (option) => option.value === field.value
                            )!.label
                          : 'Select window period'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <DropdownIcon className="w-1.4 h-1.4" />
                      </span>
                      {isNumber(field.value) && (
                        <button
                          className="absolute inset-y-0 right-3 flex items-center pr-1.6"
                          onClick={(e) => {
                            e.stopPropagation();
                            setValue('futureAppointmentWindowDays', null, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        >
                          <CloseIcon className="w-1.6 h-1.6 transform rotate-45 all-child:all-child:fill-grey" />
                        </button>
                      )}
                    </Listbox.Button>
                    <Listbox.Options className="absolute mt-0.5 max-h-16 scrollbar w-full overflow-auto rounded-[0.8rem] bg-white py-1.5 text-base shadow-primary flex flex-col gap-y-1.5 z-20">
                      {EXISTING_APPOINTMENT_WINDOW_DAYS_OPTIONS.map(
                        ({ label, value }) => (
                          <Listbox.Option
                            key={value}
                            className="relative cursor-default select-none pl-1.6"
                            value={value}
                          >
                            {({ selected }) => (
                              <span
                                className={`block truncate cursor-pointer hover:text-magenta ${
                                  selected ? 'text-magenta' : ''
                                }`}
                              >
                                {label}
                              </span>
                            )}
                          </Listbox.Option>
                        )
                      )}
                    </Listbox.Options>
                  </div>
                )}
              </Listbox>
            </div>
          )}
        />
      )}
    </div>
  );
};

export default AvoidBookingWithinTimeFrameSection;
